// SPDX-License-Identifier: Apache-2.0
import IGirafeContext from '../../tools/context/icontext';
import { LayerAttribute } from '../../models/serverwfs';
import {
  FilterOperator,
  isListFilterOperator,
  isTwoSidedFilterOperator,
  isValuelessFilterOperator,
  LogicalFilterOperator,
  logicalFilterOperators
} from '../../models/filter';
import { BaseFilterCondition } from '../../tools/filter/basefilter';
import DrawingFeature, { DrawingShape } from '../../tools/drawing/drawingFeature';
import DrawingState from '../../tools/drawing/drawingState';
import { v4 as uuidv4 } from 'uuid';
import WfsFilterCondition from '../../tools/wfs/wfsfiltercondition';
import { isAreaLikeGeometryTypeOrUnknown, isGeometry } from '../../models/xmlTypes';
import { UsedInTemplateOnly } from '../../decorators';

class FilterConditionElement {
  private readonly context: IGirafeContext;
  public id: string;
  public applied: boolean;
  public attribute?: LayerAttribute;
  public attributeDisplayName?: string;
  public attributeInputType?: string;
  public availableOperators?: { operator: FilterOperator; displayName: string }[];
  public operator?: FilterOperator;
  public value?: string;
  public value2?: string;
  public choices?: string[];
  public logicalOperator?: LogicalFilterOperator;
  public filterConditionInstance?: BaseFilterCondition<unknown>;
  private geometryChangeHandler?: (a: DrawingFeature[], b: DrawingFeature[]) => void;

  public static readonly drawingStateLocation = 'advancedFilterDrawing';
  private readonly drawingState: DrawingState;

  private readonly spatialOperatorsByDrawingShape: {
    shape: DrawingShape | null;
    supportedOperators: FilterOperator[];
  }[] = [
    { shape: DrawingShape.Point, supportedOperators: ['contains', 'intersects'] },
    { shape: DrawingShape.Polyline, supportedOperators: ['contains', 'intersects'] },
    { shape: DrawingShape.Polygon, supportedOperators: ['contains', 'intersects', 'within'] },
    { shape: DrawingShape.Square, supportedOperators: ['contains', 'intersects', 'within'] },
    { shape: DrawingShape.Rectangle, supportedOperators: ['contains', 'intersects', 'within'] },
    { shape: DrawingShape.Disk, supportedOperators: ['contains', 'intersects', 'within'] }
  ];

  constructor(context: IGirafeContext) {
    this.context = context;
    this.id = uuidv4();
    this.applied = false;

    if (!this.context.stateManager.state.extendedState[FilterConditionElement.drawingStateLocation]) {
      this.context.stateManager.state.extendedState[FilterConditionElement.drawingStateLocation] = new DrawingState();
    }
    this.drawingState = this.context.stateManager.state.extendedState[
      FilterConditionElement.drawingStateLocation
    ] as DrawingState;

    // Init available tools in the drawing toolbar
    this.drawingState.tools = this.spatialOperatorsByDrawingShape.map((c) => {
      return { shape: c.shape, enabled: false };
    });
  }

  public setLogicalOperator(operator: string) {
    this.logicalOperator = logicalFilterOperators.find((op) => op.operator === operator)?.operator;
  }

  public setAttribute(
    attribute: LayerAttribute,
    attributeDisplayName: string,
    attributeInputType: string,
    availableOperators: { operator: FilterOperator; displayName: string }[],
    choices: string[]
  ) {
    this.reset();
    this.attribute = attribute;
    this.attributeDisplayName = attributeDisplayName;
    this.attributeInputType = attributeInputType;
    this.availableOperators = availableOperators;
    this.choices = choices;
    this.updateDrawingToolbar();
  }

  public setOperator(operator: string) {
    this.operator = this.availableOperators?.find((op) => op.operator === operator)?.operator;
    if (!this.operator) {
      console.warn(`Invalid operator ${operator} for attribute ${this.attribute?.name}`);
    }
    this.updateDrawingToolbar();
  }

  public updateDrawingToolbar() {
    if (this.attribute && isGeometry(this.attribute.type)) {
      this.context.stateManager.batchChanges(() => {
        for (const drawingTool of this.drawingState.tools) {
          drawingTool.enabled = this.getSupportedDrawingToolsByOperator(this.operator).includes(drawingTool.shape);
        }
      });
      this.registerGeometryListener();
    }
  }

  private getSupportedDrawingToolsByOperator(operator?: FilterOperator): (DrawingShape | null)[] {
    return this.spatialOperatorsByDrawingShape
      .filter((c) => (operator ? c.supportedOperators.includes(operator) : false))
      .map((c) => c.shape);
  }

  public registerGeometryListener() {
    if (this.geometryChangeHandler) {
      return;
    }
    this.geometryChangeHandler = (_old, newFeatures: DrawingFeature[]) => {
      if (newFeatures.length > 0) {
        this.value = JSON.stringify({
          ...newFeatures[0].geojson,
          properties: { tool: DrawingShape[newFeatures[0].type] }
        });
      } else {
        this.value = undefined;
      }
    };
    this.context.stateManager.subscribe(
      `extendedState.${FilterConditionElement.drawingStateLocation}.features`,
      this.geometryChangeHandler
    );
  }

  public resetDrawingToolbar() {
    if (this.geometryChangeHandler) {
      this.context.stateManager.unsubscribe(this.geometryChangeHandler);
      this.geometryChangeHandler = undefined;
    }
    // Remove geometry in map
    this.drawingState.activeTool = null;
    this.drawingState.features = [];
  }

  public reset() {
    this.applied = false;
    this.attribute = undefined;
    this.attributeDisplayName = undefined;
    this.attributeInputType = undefined;
    this.operator = undefined;
    this.value = undefined;
    this.value2 = undefined;
    this.choices = undefined;
    this.availableOperators = undefined;
    this.filterConditionInstance = undefined;
    this.resetDrawingToolbar();
  }

  public remove() {
    this.resetDrawingToolbar();
  }

  public isValid(): boolean {
    return (
      !!this.attribute &&
      !!this.operator &&
      (!!this.value || isValuelessFilterOperator(this.operator)) &&
      (!!this.value2 || !isTwoSidedFilterOperator(this.operator))
    );
  }

  public apply(): boolean {
    if (this.isValid()) {
      this.filterConditionInstance = new WfsFilterCondition(
        this.attribute!.name,
        this.operator!,
        this.value,
        this.value2,
        this.attribute!.type
      );
      this.resetDrawingToolbar();
      this.applied = true;
    }
    return this.applied;
  }

  private i18n(key: string) {
    return this.context.i18nManager.getTranslation(key);
  }

  // Helper functions for UI elements

  @UsedInTemplateOnly()
  public isOperatorSelectorEnabled(): boolean {
    return !!this.attribute;
  }

  @UsedInTemplateOnly()
  public isValueInputVisible(): boolean {
    return (
      !!this.attribute &&
      (!this.operator || (this.operator && !isValuelessFilterOperator(this.operator))) &&
      !this.isGeometryToolbarVisible() &&
      !this.isChoicesListVisible()
    );
  }

  @UsedInTemplateOnly()
  public isValue2InputVisible(): boolean {
    return !!this.attribute && !!this.operator && isTwoSidedFilterOperator(this.operator);
  }

  @UsedInTemplateOnly()
  public isChoicesListVisible(): boolean {
    return !!this.attribute && !!this.choices && !!this.operator && isListFilterOperator(this.operator);
  }

  @UsedInTemplateOnly()
  public isGeometryToolbarVisible(): boolean {
    return !!this.attribute && this.attributeInputType === 'geometry';
  }

  /**
   * Check if the operator can be used with the current spatial attribute type
   */
  @UsedInTemplateOnly()
  public isSpatialOperatorEnabled(operator: FilterOperator): boolean {
    // If the operator isn't `contains`, there is no restriction necessary
    if (!this.attribute || operator !== 'contains') {
      return true;
    }
    // `Contains` only makes sense with area like geometry types
    return isAreaLikeGeometryTypeOrUnknown(this.attribute.type);
  }

  /**
   * Converts a filter condition to its text representation, using the filterCondition instances
   * toTextRepresentation method and adding the logical operator if it is defined.
   */
  @UsedInTemplateOnly()
  public toText(): string {
    if (!this.filterConditionInstance || !this.attribute) {
      return '';
    }
    const logicalOperator = this.logicalOperator
      ? logicalFilterOperators.find((op) => op.operator === this.logicalOperator)!.displayName
      : '';
    const filterExpression = this.filterConditionInstance.toTextRepresentation(this.context, this.attributeDisplayName);
    return `${this.i18n(logicalOperator)} ${filterExpression}`;
  }
}

export default FilterConditionElement;
